import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  resolveHostedUrl as resolveHostedUrlFromConstants,
  HostedUrlKey,
} from '../../constants/hosted-api-urls';
import { LocalStorageService } from './localStorage.service';

/**
 * Loyalty API client. All loyalty calls go to loyaltyURL.
 */
@Injectable({
  providedIn: 'root',
})
export class LoyaltyApiService {
  constructor(
    private http: HttpClient,
    private localStorage: LocalStorageService
  ) {}

  private resolveHostedUrl(key: HostedUrlKey): string {
    return resolveHostedUrlFromConstants(key, (k) => this.localStorage.get(k));
  }

  private get loyaltyURL(): string {
    return this.resolveHostedUrl('loyaltyURL');
  }

  getCreditUser(userId: string, restaurantId: string): Observable<any> {
    const url = `${this.loyaltyURL}/api/credit/user/${userId}?pmt_channel=loyalty_cashback&restaurant_ids=${restaurantId}`;
    return this.http.get<any>(url);
  }

  getLoyaltyPrograms(userId: string): Observable<any> {
    const url = `${this.loyaltyURL}/api/credit/loyalty/programs/${userId}`;
    return this.http.get<any>(url);
  }

  getLoyaltyOutlets(userId: string): Observable<any> {
    const url = `${this.loyaltyURL}/api/credit/loyalty/outlets/${userId}`;
    return this.http.get<any>(url);
  }

  getLoyaltyTransactions(userId: string): Observable<any> {
    const url = `${this.loyaltyURL}/api/credit/loyalty/transactions/${userId}`;
    return this.http.get<any>(url);
  }

  createCredit(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/credit`;
    return this.http.post<any>(url, params);
  }

  addCredit(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/credit/add`;
    return this.http.post<any>(url, params);
  }

  payUsingLoyalty(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/credit/loyalty`;
    return this.http.post<any>(url, params);
  }

  redeemLoyaltyOnCart(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/orders/cart/loyalty`;
    return this.http.patch<any>(url, params);
  }

  redeemLoyaltyOnOrder(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/orders/loyalty`;
    return this.http.patch<any>(url, params);
  }

  migrateLoyalty(): Observable<any> {
    const url = `${this.loyaltyURL}/api/credit/migrate-loyalty`;
    return this.http.get<any>(url);
  }

  getLoyaltySettings(hqId: string): Observable<any> {
    const url = `${this.loyaltyURL}/api/hq/${hqId}/loyalty`;
    return this.http.get<any>(url);
  }

  createLoyaltyCredit(params: any): Observable<any> {
    return this.createLoyalty(params);
  }

  /** @deprecated Use getLoyaltySettings() instead */
  getLoyalty(hqId: string): Observable<any> {
    return this.getLoyaltySettings(hqId);
  }

  createLoyalty(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/hq/loyalty`;
    return this.http.post<any>(url, params);
  }

  updateLoyalty(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/hq/loyalty`;
    return this.http.patch<any>(url, params);
  }

  getMember(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/hq/analytics/members`;
    return this.http.post<any>(url, params);
  }

  getAvgValue(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/hq/analytics/avg-value`;
    return this.http.post<any>(url, params);
  }

  getRewards(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/hq/analytics/rewards`;
    return this.http.post<any>(url, params);
  }

  getRevenue(params: any): Observable<any> {
    const url = `${this.loyaltyURL}/api/hq/analytics/revenue`;
    return this.http.post<any>(url, params);
  }
}
